import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useArticleName } from '@/models/article';
import { ShipmentWithRelations, useVerifyArticle } from '@/models/shipment';

const ReportIssueDialog = ({
  shipmentArticle,
  isOpen,
  setIsOpen,
}: {
  shipmentArticle: ShipmentWithRelations['articles'][number];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const { t } = useLingui();

  return (
    <Dialog
      title={t({ id: 'report-issue-modal.title', message: 'Report a shipment issue' })}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <ReportIssueForm shipmentArticle={shipmentArticle} close={() => setIsOpen(false)} />
    </Dialog>
  );
};

const ReportIssueForm = ({
  shipmentArticle,
  close,
}: {
  shipmentArticle: ShipmentWithRelations['articles'][number];
  close: () => void;
}) => {
  const { t } = useLingui();

  const [issue, setIssue] = useState(shipmentArticle.issue ?? '');
  const [showErrors, setShowErrors] = useState(false);

  const articleName = useArticleName({ article: shipmentArticle.article });

  const {
    mutateAsync: verifyArticle,
    isPending,
    isSuccess,
  } = useVerifyArticle({
    shipmentId: shipmentArticle.shipmentId,
    articleId: shipmentArticle.articleId,
  });

  const save = async () => {
    setShowErrors(true);

    if (!issue) {
      return;
    }

    await verifyArticle({
      verified: true,
      issue,
    });

    close();
  };

  return (
    <>
      <main>
        <Stack gap="1rem">
          <Paragraph color="secondary">
            <Trans id="report-issue-modal.intro">
              If you notice a shipment issue with the <b>{articleName}</b> item (damaged,
              missing...), please let us know below:
            </Trans>
          </Paragraph>
          <TextArea
            ariaLabel={t({
              id: 'report-issue-modal.label',
              message: 'Shipment issue description',
            })}
            placeholder={t({
              id: 'report-issue-modal.placeholder',
              message: 'Describe the issue...',
            })}
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            error={
              showErrors && !issue
                ? t({
                    id: 'report-issue-modal.error',
                    message: 'Please describe the shipment issue',
                  })
                : undefined
            }
            rows={4}
          />
        </Stack>
      </main>
      <Footer>
        <FooterButtons>
          <Button variant="secondary" size="medium" onPress={close}>
            <Trans id="report-issue-modal.actions.cancel">Cancel</Trans>
          </Button>
          <Button variant="danger" size="medium" onPress={save} isLoading={isPending || isSuccess}>
            <Trans id="report-issue-modal.actions.report">Report issue</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};

export default ReportIssueDialog;
